import { useId, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import { classify, type Answers, type Sector, type Size } from '@/lib/nisgCheck'
import { contactStore, sectionStore } from '@/lib/variables'
import { transitionTo } from '@/lib/transitions'
import './NisgCheck.css'

/**
 * "Does the NISG 2026 apply to you?" Three questions, a first verdict with
 * the duties that follow from it, and a button that hands the answers to the
 * contact form. Lives in the cybersecurity service panel (`ask="scroll"`,
 * the shell scrolls to the form) and on /nisg-2026 (`ask="navigate"`).
 * Copy: content/nisgCheck.json; rules: lib/nisgCheck.ts.
 */

type Group = Exclude<Sector, ''>
type Copy = {
  title: string
  intro: string
  sectorLabel: string
  sectorPlaceholder: string
  sectorGroups: Record<Group, string>
  sectors: Record<Group, string[]>
  sizeLabel: string
  sizes: Record<Size, string>
  specialLabel: string
  supplierLabel: string
  resultsTitle: string
  results: Record<string, { title: string; text: string }>
  dutiesTitle: string
  duties: Record<'covered' | 'supplier' | 'none', string[]>
  cta: string
  ctaNone: string
  more: string
  prefill: string
}

const DUTIES_FOR = { essential: 'covered', important: 'covered', special: 'covered', supplier: 'supplier', none: 'none' } as const

export default function NisgCheck({ ask, showMore = false }: { ask: 'scroll' | 'navigate'; showMore?: boolean }) {
  const { section, t, to } = useI18n()
  const navigate = useNavigate()
  const c = section('nisgCheck') as unknown as Copy
  const id = useId()

  const [sectorName, setSectorName] = useState('')
  const [answers, setAnswers] = useState<Answers>({ sector: '', size: '', special: false, supplier: false })
  const set = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }))

  const verdict = classify(answers)
  const result = verdict ? c.results[verdict] : null

  const request = () => {
    const sizeText = answers.size ? c.sizes[answers.size] : '–'
    const summary = c.prefill
      .replace('{sector}', sectorName || '–')
      .replace('{size}', sizeText)
      .replace('{result}', result?.title ?? '–')
    contactStore.prefill({ subject: t('inquiry.nisgSubject'), message: `${summary}\n\n${t('inquiry.nisgMessage')}` })
    if (ask === 'scroll') sectionStore.go('contact')
    else transitionTo(navigate, to('/contact'))
  }

  return (
    <section className="nisg-check" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="nisg-check__title">
        {c.title}
      </h3>
      <p className="nisg-check__intro">{c.intro}</p>

      <div className="nisg-check__grid">
        <label className="nisg-check__field">
          <span className="nisg-check__label mono">{c.sectorLabel}</span>
          <select
            className="input nisg-check__select"
            value={sectorName}
            onChange={(e) => {
              const name = e.target.value
              const group = (['essential', 'important', 'none'] as Group[]).find((g) => c.sectors[g].includes(name)) ?? ''
              setSectorName(name)
              set({ sector: group })
            }}
          >
            <option value="">{c.sectorPlaceholder}</option>
            {(['essential', 'important', 'none'] as Group[]).map((g) => (
              <optgroup key={g} label={c.sectorGroups[g]}>
                {c.sectors[g].map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        <fieldset className="nisg-check__field nisg-check__fieldset">
          <legend className="nisg-check__label mono">{c.sizeLabel}</legend>
          {(['small', 'medium', 'large'] as Size[]).map((s) => (
            <label key={s} className="nisg-check__option">
              <input type="radio" name={`${id}-size`} checked={answers.size === s} onChange={() => set({ size: s })} />
              <span>{c.sizes[s]}</span>
            </label>
          ))}
        </fieldset>
      </div>

      <label className="nisg-check__option">
        <input type="checkbox" checked={answers.special} onChange={(e) => set({ special: e.target.checked })} />
        <span>{c.specialLabel}</span>
      </label>
      <label className="nisg-check__option">
        <input type="checkbox" checked={answers.supplier} onChange={(e) => set({ supplier: e.target.checked })} />
        <span>{c.supplierLabel}</span>
      </label>

      {verdict && result && (
        <div className={`nisg-check__result is-${verdict}`} role="status" aria-live="polite">
          <span className="eyebrow eyebrow--plain">{c.resultsTitle}</span>
          <h4 className="nisg-check__verdict">{result.title}</h4>
          <p>{result.text}</p>
          <p className="nisg-check__label mono">{c.dutiesTitle}</p>
          <ul className="nisg-check__duties">
            {c.duties[DUTIES_FOR[verdict]].map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
          <div className="nisg-check__actions">
            <button type="button" className="btn btn--primary btn--sm" onClick={request}>
              {verdict === 'none' ? c.ctaNone : c.cta}
              <span className="arrow" aria-hidden>
                →
              </span>
            </button>
            {showMore && (
              <Link to={to('/nisg-2026')} className="nisg-check__more">
                {c.more}
              </Link>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
